import {
  AddGitHubIntegrationProjectRequestConfig,
  CreateGitHubIntegrationConfigRequestConfig,
  DeleteGitHubIntegrationConfigRequestConfig,
  GitHubDiscoverRequestConfig,
  type GitHubIntegrationConfig,
  GitHubIntegrationConfigsRequestConfig,
  type GitHubRepository,
  type GitHubSyncMode,
  type GitHubWebhookInfo,
  GitHubWebhookRequestConfig,
  RemoveGitHubIntegrationProjectRequestConfig,
  UpdateGitHubCredentialsRequestConfig,
  UpdateGitHubSyncModeRequestConfig,
  UpdateGitHubWebhookSecretRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { err, ok, type Result } from "neverthrow";
import { isServer } from "solid-js/web";
import { type FeatureAction, trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

export type { GitHubIntegrationConfig, GitHubRepository, GitHubSyncMode, GitHubWebhookInfo } from "@storytell/client";

interface CreateGitHubIntegrationInput {
  projectIds: string[];
  appId: string;
  privateKey: string;
  accountLogin: string;
}

class GitHubIntegrationError extends Error {
  readonly errorResult: { code?: string | number; message?: string; statusCode?: number };

  constructor(payload: { code?: string | number; message?: string; statusCode?: number }) {
    const message = payload.message ?? "Request failed";
    super(message);
    this.name = "GitHubIntegrationError";
    this.errorResult = {
      code: payload.code,
      message,
      statusCode: payload.statusCode,
    };
  }
}

const toError = (e: Error): GitHubIntegrationError => {
  const meta = e as Error & { code?: string; status?: number };
  return new GitHubIntegrationError({
    code: meta.code ?? meta.status,
    message: e.message,
    statusCode: meta.status,
  });
};

export function useGitHub() {
  const wire = useWire();
  const client = useStorytellClient();

  const send = async <T>(
    doFetch: () => Promise<Result<T, Error>>,
    action?: FeatureAction<"integration.github">
  ): Promise<Result<T, GitHubIntegrationError>> => {
    if (isServer) return err(new GitHubIntegrationError({ statusCode: 0 }));
    const res = (await doFetch()).mapErr(toError);
    if (action && res.isOk()) trackFeature({ feature: "integration.github", action, via: "button" });
    return res;
  };

  const listConfigs = async (): Promise<Result<GitHubIntegrationConfig[], GitHubIntegrationError>> => {
    const res = await send(() =>
      client.fetch(GitHubIntegrationConfigsRequestConfig.withBearerToken(wire.auth.token()), undefined, undefined, {
        orgIdOverride: wire.currentOrgId(),
      })
    );
    // Grants maps out-of-scope reads to 404, so non-admins get an empty list
    // instead of an error.
    if (res.isErr() && res.error.errorResult.statusCode === 404) return ok([]);
    return res;
  };

  // The reply's setupComplete is false: the webhook URL is derived from the new
  // config's id, so the admin registers it and returns with the webhook secret.
  const createConfig = (
    input: CreateGitHubIntegrationInput
  ): Promise<Result<GitHubIntegrationConfig, GitHubIntegrationError>> =>
    send(
      () =>
        client.fetch(CreateGitHubIntegrationConfigRequestConfig.withBearerToken(wire.auth.token()), undefined, input, {
          orgIdOverride: wire.currentOrgId(),
        }),
      "connect"
    );

  const deleteConfig = (integrationId: string): Promise<Result<void, GitHubIntegrationError>> =>
    send(
      () =>
        client.fetch(
          DeleteGitHubIntegrationConfigRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          undefined,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "disconnect"
    );

  // The backend mints an installation token from the credentials before storing
  // them and clears authBrokenAt.
  const updateCredentials = (
    integrationId: string,
    input: { appId: string; privateKey: string; accountLogin: string }
  ): Promise<Result<GitHubIntegrationConfig, GitHubIntegrationError>> =>
    send(
      () =>
        client.fetch(
          UpdateGitHubCredentialsRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          input,
          {
            orgIdOverride: wire.currentOrgId(),
          }
        ),
      "reconnect"
    );

  // A new deployment starts with an empty repository selection, so nothing syncs
  // until the settings panel picks one.
  const addProject = (
    integrationId: string,
    projectId: string
  ): Promise<Result<GitHubIntegrationConfig, GitHubIntegrationError>> =>
    send(
      () =>
        client.fetch(
          AddGitHubIntegrationProjectRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          { projectId },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "deploy"
    );

  const removeProject = (integrationId: string, projectId: string): Promise<Result<void, GitHubIntegrationError>> =>
    send(
      () =>
        client.fetch(
          RemoveGitHubIntegrationProjectRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId, projectId },
          undefined,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "undeploy"
    );

  // allowPrune must be true for a narrowing change; the backend rejects an
  // unconfirmed narrowing with 409.
  const updateSyncMode = (
    integrationId: string,
    projectId: string,
    input: { syncMode: GitHubSyncMode; selectedRepoIds: string[]; allowPrune: boolean }
  ): Promise<Result<GitHubIntegrationConfig, GitHubIntegrationError>> =>
    send(
      () =>
        client.fetch(
          UpdateGitHubSyncModeRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId, projectId },
          input,
          { orgIdOverride: wire.currentOrgId() }
        ),
      "configure"
    );

  const discover = async (integrationId: string): Promise<Result<GitHubRepository[], GitHubIntegrationError>> => {
    const res = await send(() =>
      client.fetch(GitHubDiscoverRequestConfig.withBearerToken(wire.auth.token()), { integrationId }, undefined, {
        orgIdOverride: wire.currentOrgId(),
      })
    );
    return res.map((value) => value.repositories);
  };

  const getWebhook = (integrationId: string): Promise<Result<GitHubWebhookInfo, GitHubIntegrationError>> =>
    send(() =>
      client.fetch(GitHubWebhookRequestConfig.withBearerToken(wire.auth.token()), { integrationId }, undefined, {
        orgIdOverride: wire.currentOrgId(),
      })
    );

  // GitHub shows the webhook secret only where the customer set it, so it arrives
  // as a second paste. Storing it completes setup.
  const saveWebhookSecret = (
    integrationId: string,
    secret: string
  ): Promise<Result<GitHubIntegrationConfig, GitHubIntegrationError>> =>
    send(
      () =>
        client.fetch(
          UpdateGitHubWebhookSecretRequestConfig.withBearerToken(wire.auth.token()),
          { integrationId },
          { secret },
          { orgIdOverride: wire.currentOrgId() }
        ),
      "configure"
    );

  return {
    listConfigs,
    createConfig,
    deleteConfig,
    updateCredentials,
    addProject,
    removeProject,
    updateSyncMode,
    discover,
    getWebhook,
    saveWebhookSecret,
  };
}
